import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@xstyled/styled-components';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material';
import { loginUser } from '../../store/slices/authSlice';
import axios from 'axios';
import { API_URL } from '../../config';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { ColorModeContext } from '../../utils/theme';
import { ThemeToggle, ThemeToggleIcon } from '../../components/TopBarStyles';

const LoginContainer = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px 12px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? `radial-gradient(circle at 20% 15%, rgba(130,170,255,.30), transparent 55%),
       radial-gradient(circle at 80% 25%, rgba(128,203,196,.22), transparent 55%),
       radial-gradient(circle at 55% 90%, rgba(255,83,112,.10), transparent 60%),
       radial-gradient(circle at top left, #20263a 0, #0b0d15 45%, #050610 100%)`
    : props.theme.palette.background.body};
  overflow: hidden;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
  color: ${props => props.theme.palette.text.primary};
  box-sizing: border-box;
`;

const Card = styled.div`
  width: min(460px, 100%);
  padding: 28px 20px 22px;
  border-radius: 24px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(130,170,255,.10), rgba(11,13,21,.96))'
    : props.theme.palette.background.app};
  box-shadow: ${props => props.theme.palette.mode === 'dark'
    ? '0 18px 45px rgba(0,0,0,.55)'
    : '0 18px 45px rgba(0,0,0,.1)'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark'
    ? 'rgba(130,170,255,.18)'
    : 'rgba(130,170,255,.1)'};
  backdrop-filter: blur(10px);
`;

const CardInner = styled.div`
  border-radius: 18px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'rgba(12,14,23,.92)'
    : props.theme.palette.background.appInner};
  border: 1px solid ${props => props.theme.palette.mode === 'dark'
    ? '#23283b'
    : 'rgba(0,0,0,0.05)'};
  padding: 22px 18px;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  margin-bottom: 18px;
`;

const Logo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-weight: 950;
  color: ${props => props.theme.palette.mode === 'dark' ? '#050713' : '#fff'};
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'radial-gradient(circle at 25% 0, #82aaff, #151827)'
    : `linear-gradient(135deg, ${props.theme.palette.primary.main}, ${props.theme.palette.primary.dark})`};
  box-shadow: ${props => props.theme.palette.mode === 'dark'
    ? '0 0 18px rgba(130,170,255,.55)'
    : '0 4px 12px rgba(15, 118, 110, 0.3)'};
  letter-spacing: .06em;
  transform: translateY(5px);
`;

const Title = styled.div`
  font-family: "Courier New", Courier, "Lucida Sans Typewriter", "Lucida Typewriter", monospace;
  font-weight: 700;
  letter-spacing: .06em;
  font-size: 18px;
  line-height: 1.1;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'linear-gradient(90deg, rgba(130,170,255,.95), rgba(128,203,196,.92))'
    : `linear-gradient(90deg, ${props.theme.palette.primary.dark}, ${props.theme.palette.primary.main})`};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: ${props => props.theme.palette.mode === 'dark'
    ? '0 0 18px rgba(130,170,255,.10)'
    : 'none'};
  margin-bottom: 14px;
`;

const Badge = styled.span`
  font-size: 10px;
  letter-spacing: .18em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128,203,196,.60)' : props.theme.palette.primary.light};
  color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : props.theme.palette.primary.dark};
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'rgba(5,16,18,.75)'
    : props.theme.palette.primary[100]};
`;

const LoginGap = styled.div`
  height: 12px;
`;

const Form = styled.form`
  display: grid;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 12px;
  color: ${props => props.theme.palette.text.secondary};
  margin-bottom: 6px;
  display: block;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 10px;
  border-radius: 14px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'rgba(5,7,16,.86)'
    : '#fff'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark'
    ? '#20253a'
    : props.theme.palette.grey[300]};
  transition: border-color .18s ease-out, box-shadow .18s ease-out, transform .18s ease-out;

  &:focus-within {
    border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130,170,255,.70)' : props.theme.palette.primary.main};
    box-shadow: ${props => props.theme.palette.mode === 'dark'
    ? '0 0 0 3px rgba(130,170,255,.14)'
    : `0 0 0 3px ${props.theme.palette.primary[100]}`};
    transform: translateY(-1px);
  }
`;

const Icon = styled.svg`
  width: 18px;
  height: 18px;
  color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128,203,196,.9)' : props.theme.palette.text.secondary};
  flex: 0 0 auto;
`;

const Input = styled.input`
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${props => props.theme.palette.text.primary};
  font-size: 14px;
  font-family: inherit;

  &::placeholder {
    color: ${props => props.theme.palette.text.secondary};
    opacity: 0.7;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px ${props => props.theme.palette.mode === 'dark' ? '#050710' : '#ffffff'} inset !important;
    -webkit-text-fill-color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'} !important;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'} !important;
    caret-color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
  }
`;

const ShowButton = styled.button`
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? '#252a3e' : 'rgba(0,0,0,0.1)'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(17,20,33,.96)' : 'rgba(255,255,255,0.8)'};
  color: ${props => props.theme.palette.text.secondary};
  border-radius: 999px;
  border-radius: 999px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  transition: transform .18s ease-out, border-color .18s ease-out, color .18s ease-out, box-shadow .18s ease-out;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(130,170,255,.7);
    color: ${props => props.theme.palette.text.primary};
    box-shadow: 0 0 12px rgba(130,170,255,.18);
  }
`;

const SubmitButton = styled.button`
  margin-top: 8px;
  width: 100%;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130,170,255,.55)' : 'transparent'};
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'linear-gradient(90deg, rgba(130,170,255,.22), rgba(128,203,196,.12))'
    : `linear-gradient(90deg, ${props.theme.palette.primary.main}, ${props.theme.palette.primary.dark})`};
  color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#fff'};
  font-size: 14px;
  font-weight: 750;
  cursor: pointer;
  transition: transform .18s ease-out, box-shadow .18s ease-out, border-color .18s ease-out;

  &:hover {
    transform: translateY(-1px);
    border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128,203,196,.85)' : 'transparent'};
    box-shadow: ${props => props.theme.palette.mode === 'dark'
    ? '0 0 14px rgba(130,170,255,.20)'
    : `0 4px 12px ${props.theme.palette.primary[200]}`};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  color: #ff5370;
  font-size: 13px;
  text-align: center;
  margin-bottom: 12px;
  background: rgba(255, 83, 112, 0.1);
  padding: 8px;
  border-radius: 8px;
  border: 1px solid rgba(255, 83, 112, 0.2);
`;

const ToggleContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
`;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  // const { error } = useSelector((state) => state.auth); // authSlice doesn't have error state currently
  const [error, setError] = useState(null); // Local error state
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors

    try {
      const response = await axios.post(`${API_URL}/login`, {
        username: email, // Using email state for username/email input
        password: password
      });

      if (response.status === 200) {
        console.log('Login successful', response.data);
        dispatch(loginUser(true)); // passing true for rememberMe as default for now
        navigate('/economic-calendar');
      }
    } catch (err) {
      console.error('Login failed', err);
      setError(err.response?.data?.detail || 'Invalid credentials');
    }
  };

  return (
    <LoginContainer>
      <ToggleContainer>
        <ThemeToggle onClick={colorMode.toggleColorMode} type="button" aria-label="Toggle light/dark mode">
          <ThemeToggleIcon>
            {theme.palette.mode === 'dark' ? '☾' : '☀'}
          </ThemeToggleIcon>
        </ThemeToggle>
      </ToggleContainer>
      <Card>
        <CardInner>
          <Brand>
            <Logo>TT</Logo>
            <Title>Trade Terminal</Title>
            <Badge>Login</Badge>
          </Brand>

          <LoginGap aria-hidden="true" />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Form onSubmit={handleLogin} noValidate>
            <div>
              <Label htmlFor="email">Email or Username</Label>
              <InputWrapper>
                <Icon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M4 7.5 12 13l8-5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 7.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </Icon>
                <Input
                  id="email"
                  type="text"
                  placeholder="name@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </InputWrapper>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <InputWrapper>
                <Icon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M6.5 11h11A2.5 2.5 0 0 1 20 13.5v5A2.5 2.5 0 0 1 17.5 21h-11A2.5 2.5 0 0 1 4 18.5v-5A2.5 2.5 0 0 1 6.5 11Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 15v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </Icon>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  minLength="6"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <ShowButton type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </ShowButton>
              </InputWrapper>
            </div>

            <SubmitButton type="submit">Login</SubmitButton>
          </Form>
        </CardInner>
      </Card>
    </LoginContainer>
  );
};

export default Login;
